import { ApiSchemas } from '..'
import { client } from '../client'
import { wrapEndpoint } from '../wrap'

export enum AttendanceStatus {
  IN_PROGRESS = 'A',
  FINISHED = 'F',
  CANCELLED = 'C',
}

export type Attendance = ApiSchemas['AttendancePresenter']

export default {
  async getById(id: number) {
    return wrapEndpoint(() =>
      client.GET('/attendance/{id}', {
        params: {
          path: { id },
        },
      }),
    )
  },
  async list(options?: ApiSchemas['ListAttendanceDto']) {
    return wrapEndpoint(() =>
      client.GET('/attendance', {
        params: {
          query: options,
        },
      }),
    )
  },
  async create(body: ApiSchemas['CreateAttendanceDto']) {
    return wrapEndpoint(() =>
      client.POST('/attendance', {
        body,
      }),
    )
  },
  async duplicate(id: number, body: ApiSchemas['DuplicateAttendanceDto']) {
    return wrapEndpoint(() =>
      client.POST('/attendance/{id}/duplicate', {
        params: {
          path: {
            id,
          },
        },
        body,
      }),
    )
  },
  async updateAnswers(id: number, answers: ApiSchemas['UpsertAnswerDto'][]) {
    await wrapEndpoint(() =>
      client.PUT('/attendance/{id}/answer', {
        params: {
          path: {
            id,
          },
        },
        body: {
          answers,
        },
      }),
    )
  },
  async generateAiMessages(id: number, questionIds: number[], answers: ApiSchemas['UpsertAnswerDto'][]) {
    return wrapEndpoint(() =>
      client.PATCH('/attendance/{id}/generate-ai-messages', {
        body: {
          answers,
          questionIds,
        },
        params: {
          path: {
            id,
          },
        },
      }),
    )
  },
  async finish(id: number) {
    await wrapEndpoint(() =>
      client.PATCH('/attendance/{id}/finish', {
        params: {
          path: {
            id,
          },
        },
      }),
    )
  },
  async delete(id: number) {
    await wrapEndpoint(() =>
      client.DELETE('/attendance/{id}', {
        params: {
          path: {
            id,
          },
        },
      }),
    )
  },
  async share(id: number, body: ApiSchemas['ShareAttendanceDto']) {
    return wrapEndpoint(() =>
      client.PATCH('/attendance/{id}/share', {
        params: {
          path: {
            id,
          },
        },
        body,
      }),
    )
  },
  async listUnreadStandaloneAnswers(id: number) {
    return wrapEndpoint(() =>
      client.GET('/attendance/{id}/unread-standalone-answer', {
        params: {
          path: {
            id,
          },
        },
      }),
    )
  },
  async createStandaloneAnswer(id: number, body: ApiSchemas['CreateAttendanceStandaloneAnswerDto']) {
    await wrapEndpoint(() =>
      client.POST('/attendance/{id}/standalone-answer', {
        params: {
          path: {
            id,
          },
        },
        body,
      }),
    )
  },
  async uploadFiles(id: number, body: ApiSchemas['UploadAttendanceFilesViaShareTokenDto']) {
    await wrapEndpoint(() =>
      client.POST('/attendance/{id}/file', {
        params: {
          path: {
            id,
          },
        },
        body,
        bodySerializer: (body) => {
          const formData = new FormData()
          body.files.forEach((file, index) => {
            formData.append(`files.${index}.name`, file.name)
            formData.append(`files.${index}.description`, file.description ?? 'null')
            formData.append(`files.${index}.referenceDate`, file.referenceDate ?? 'null')
            formData.append(`files.${index}.fileData`, file.fileData as unknown as File)
          })
          return formData
        },
      }),
    )
  },
  async uploadFilesViaShareToken(id: number, body: ApiSchemas['UploadAttendanceFilesViaShareTokenDto']) {
    await wrapEndpoint(() =>
      client.POST('/attendance/{id}/via-share-token/file', {
        params: {
          path: {
            id,
          },
        },
        body,
        bodySerializer: (body) => {
          const formData = new FormData()
          body.files.forEach((file, index) => {
            formData.append(`files.${index}.name`, file.name)
            formData.append(`files.${index}.description`, file.description ?? 'null')
            formData.append(`files.${index}.referenceDate`, file.referenceDate ?? 'null')
            formData.append(`files.${index}.fileData`, file.fileData as unknown as File)
          })
          return formData
        },
      }),
    )
  },
}
